/* Configurazione globale del Fascicolo Privacy.
 * 2 liste ordinate (drag-free, con frecce su/giù): modelli da generare per ogni TRATTAMENTO
 * e modelli da generare per ogni DOCENTE.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

export default function PrivacyFascicoloConfigPage() {
  const [tratt, setTratt] = useState([]);       // [{codice, nome}]
  const [docen, setDocen] = useState([]);
  const [ester, setEster] = useState([]);
  const [modelliTratt, setModelliTratt] = useState([]);
  const [modelliDoc, setModelliDoc] = useState([]);
  const [modelliEst, setModelliEst] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const carica = async () => {
    setLoading(true);
    try {
      // Filtro per SEZIONE del fascicolo (Trattamento / Anagrafica / Esterni):
      // ogni dropdown mostra solo i modelli del Catalogo Docx che hanno quella
      // sezione marcata nel campo `sezioni`.
      const [cfg, rt, rd, re] = await Promise.all([
        api.get("/privacy/fascicolo/config"),
        api.get("/privacy/modelli", { params: { sezione: "trattamento" } }),
        api.get("/privacy/modelli", { params: { sezione: "anagrafica" } }),
        api.get("/privacy/modelli", { params: { sezione: "esterno" } }),
      ]);
      const allT = rt.data?.items || [];
      const allD = rd.data?.items || [];
      const allE = re.data?.items || [];
      setModelliTratt(allT);
      setModelliDoc(allD);
      setModelliEst(allE);
      const idxT = Object.fromEntries(allT.map(m => [m.codice, m]));
      const idxD = Object.fromEntries(allD.map(m => [m.codice, m]));
      const idxE = Object.fromEntries(allE.map(m => [m.codice, m]));
      setTratt((cfg.data?.trattamento || []).map(c => idxT[c] || { codice: c, nome: "(modello non in sezione 'trattamento')" }));
      setDocen((cfg.data?.docente || []).map(c => idxD[c] || { codice: c, nome: "(modello non in sezione 'anagrafica')" }));
      setEster((cfg.data?.esterno || []).map(c => idxE[c] || { codice: c, nome: "(modello non in sezione 'esterno')" }));
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Errore caricamento" });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { carica(); }, []);

  const salva = async () => {
    setSaving(true); setMsg(null);
    try {
      await api.put("/privacy/fascicolo/config", {
        trattamento: tratt.map(m => m.codice),
        docente: docen.map(m => m.codice),
        esterno: ester.map(m => m.codice),
      });
      setMsg({ ok: true, text: "Configurazione salvata. Tutti i nuovi fascicoli useranno queste liste." });
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Errore salvataggio" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16, gap: 12 }}>
        <h2 style={{ color: "#37474f", fontSize: 18, margin: 0 }}>📁 Configurazione Fascicoli Privacy</h2>
        <button onClick={carica} disabled={loading} style={{ padding: "6px 12px", background: "#455a64", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
          🔄 Ricarica
        </button>
        <button onClick={salva} disabled={saving || loading} data-testid="fasc-cfg-save"
          style={{ marginLeft: "auto", padding: "8px 18px", background: "#2e7d32", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1 }}>
          {saving ? "⏳ Salvataggio..." : "💾 Salva configurazione"}
        </button>
      </div>

      <div style={{ background: "#fff8e1", border: "1px solid #ffe082", padding: 10, fontSize: 11, color: "#6d4c00", marginBottom: 16 }}>
        <b>Come funziona:</b> esistono 3 fascicoli — <b>Trattamento</b>, <b>Anagrafica</b> (docente), <b>Esterni</b>.
        Per ogni entità (trattamento / docente / azienda esterna) verranno generati in serie i PDF dei modelli elencati nella lista corrispondente.
        L&apos;ordine della lista determina l&apos;ordine dei documenti nel fascicolo finale.
        Salva qui, poi torna su Trattamenti/Docenti/Esterni e clicca 📁 sulla riga per generare il fascicolo.
      </div>

      {msg && (
        <div style={{ padding: 10, background: msg.ok ? "#e8f5e9" : "#fff0f0", color: msg.ok ? "#1b5e20" : "#c00", fontSize: 12, marginBottom: 12, border: `1px solid ${msg.ok ? "#a5d6a7" : "#ef9a9a"}` }}>
          {msg.text}
          <button onClick={() => setMsg(null)} style={{ float: "right", background: "none", border: "none", cursor: "pointer", color: msg.ok ? "#1b5e20" : "#c00", fontWeight: 700 }}>✕</button>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
        <ListEditor
          title="📋 Fascicolo TRATTAMENTO"
          color="#1565c0"
          selected={tratt}
          setSelected={setTratt}
          allModelli={modelliTratt}
          testidPrefix="fasc-cfg-tratt"
        />
        <ListEditor
          title="👤 Fascicolo ANAGRAFICA (docente)"
          color="#6a1b9a"
          selected={docen}
          setSelected={setDocen}
          allModelli={modelliDoc}
          testidPrefix="fasc-cfg-doc"
        />
        <ListEditor
          title="🌐 Fascicolo ESTERNI"
          color="#00838f"
          selected={ester}
          setSelected={setEster}
          allModelli={modelliEst}
          testidPrefix="fasc-cfg-est"
        />
      </div>
    </div>
  );
}

function ListEditor({ title, color, selected, setSelected, allModelli, testidPrefix }) {
  const [sel, setSel] = useState("");
  const selectedCodes = new Set(selected.map(m => m.codice));
  const available = allModelli.filter(m => !selectedCodes.has(m.codice));

  const add = () => {
    if (!sel) return;
    const m = allModelli.find(x => x.codice === sel);
    if (!m || selectedCodes.has(m.codice)) return;
    setSelected([...selected, m]);
    setSel("");
  };
  const remove = (idx) => setSelected(selected.filter((_, i) => i !== idx));
  const move = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= selected.length) return;
    const copy = [...selected];
    [copy[idx], copy[target]] = [copy[target], copy[idx]];
    setSelected(copy);
  };

  return (
    <div style={{ background: "white", border: `1px solid ${color}`, padding: 12 }}>
      <div style={{ fontWeight: 700, fontSize: 13, color, marginBottom: 10 }}>{title} · {selected.length} modelli</div>
      <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
        <select value={sel} onChange={e => setSel(e.target.value)} data-testid={`${testidPrefix}-select`}
          style={{ flex: 1, padding: "5px 8px", border: "1px solid #ccc", fontSize: 12 }}>
          <option value="">— scegli un modello da aggiungere —</option>
          {available.map(m => (
            <option key={m.codice} value={m.codice}>{m.codice} — {m.nome}{m.filename ? "" : " (no file)"}</option>
          ))}
        </select>
        <button onClick={add} disabled={!sel} data-testid={`${testidPrefix}-add`}
          style={{ padding: "5px 12px", background: color, color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: sel ? "pointer" : "not-allowed", opacity: sel ? 1 : 0.5 }}>
          ➕ Aggiungi
        </button>
      </div>
      <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
        <thead className="thead-blue">
          <tr>
            <th style={{ width: 24, padding: 4 }}>#</th>
            <th style={{ width: 70, textAlign: "left", padding: 4 }}>CODICE</th>
            <th style={{ textAlign: "left", padding: 4 }}>NOME MODELLO</th>
            <th style={{ width: 90, padding: 4 }}>AZIONI</th>
          </tr>
        </thead>
        <tbody>
          {selected.length === 0 && (
            <tr><td colSpan={4} style={{ padding: 20, textAlign: "center", color: "#888" }}>Nessun modello aggiunto.</td></tr>
          )}
          {selected.map((m, i) => (
            <tr key={m.codice} style={{ borderTop: "1px solid #eee" }} data-testid={`${testidPrefix}-row-${i}`}>
              <td style={{ textAlign: "center", color: "#888", padding: 4 }}>{i + 1}</td>
              <td style={{ fontFamily: "Consolas, monospace", fontWeight: 700, color: "#0d47a1", padding: 4 }}>{m.codice}</td>
              <td style={{ padding: 4 }}>{m.nome}</td>
              <td style={{ textAlign: "center", padding: 4, whiteSpace: "nowrap" }}>
                <button onClick={() => move(i, -1)} disabled={i === 0}
                  title="Sposta su"
                  style={{ background: "#eee", border: "none", padding: "2px 6px", fontSize: 11, cursor: i === 0 ? "not-allowed" : "pointer", marginRight: 2, opacity: i === 0 ? 0.4 : 1 }}>↑</button>
                <button onClick={() => move(i, 1)} disabled={i === selected.length - 1}
                  title="Sposta giù"
                  style={{ background: "#eee", border: "none", padding: "2px 6px", fontSize: 11, cursor: i === selected.length - 1 ? "not-allowed" : "pointer", marginRight: 2, opacity: i === selected.length - 1 ? 0.4 : 1 }}>↓</button>
                <button onClick={() => remove(i)} data-testid={`${testidPrefix}-remove-${i}`}
                  title="Rimuovi"
                  style={{ background: "#c62828", color: "white", border: "none", padding: "2px 8px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>✕</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
